/**
 * O que faz: Card compacto da campanha de follow-up: status com interruptor, quando dispara,
 *            linha do tempo da sequência (cada mensagem abre sua prévia), restrições ligadas e ações.
 * Onde usa: Lista "Campanhas" em FollowupManagement.
 * Conexões: useUpdateFollowupCampaignStatus, useDeleteFollowupCampaign e helpers de
 *           src/features/followup/apresentacao.ts. Visual do Gens Design System (.gens-escopo).
 * Tabelas: follow_up_campaigns (UPDATE de "ativo" e DELETE) via hooks.
 * Autor: IA
 * Data: 28/04/2026 (redesenho compacto no design system em 07/10/2026).
 */

import { useId, useState } from 'react';
import { Clock3, Copy, Image as ImageIcon, Loader2, PencilLine, ShieldCheck, Sparkles, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDeleteFollowupCampaign, useUpdateFollowupCampaignStatus } from '@/hooks/useFollowupData';
import {
  listarRestricoesFollowup,
  montarLinhaDoTempoFollowup,
  resumirGatilhoFollowup,
} from '@/features/followup/apresentacao';
import { cn } from '@/lib/utils';
import type { FollowupCampaign } from '@/types/followup';

interface CampaignCardProps {
  campaign: FollowupCampaign;
  onEdit: (campaign: FollowupCampaign) => void;
  onDuplicate: (campaign: FollowupCampaign) => void;
}

export const CampaignCard = ({ campaign, onEdit, onDuplicate }: CampaignCardProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Mensagem da sequência com a prévia aberta (null = nenhuma). Só uma por vez para o card não crescer demais.
  const [passoAberto, setPassoAberto] = useState<number | null>(null);
  const idBase = useId();
  const updateStatus = useUpdateFollowupCampaignStatus();
  const removeCampaign = useDeleteFollowupCampaign();

  const passos = montarLinhaDoTempoFollowup(campaign.janela_inatividade_minutos, campaign.follow_up_templates ?? []);
  const restricoes = listarRestricoesFollowup(campaign);
  const passoSelecionado = passos.find((passo) => passo.sequencia === passoAberto) ?? null;
  const idPrevia = `${idBase}-previa`;

  const handleConfirmRemove = () => {
    // Fecha o modal somente no sucesso para nao dar impressao de que apagou quando falha.
    // Motivo: se houver erro de permissao/regra, o usuario enxerga o estado real da tela.
    removeCampaign.mutate(campaign.id, {
      onSuccess: () => setDeleteDialogOpen(false),
    });
  };

  return (
    <article
      aria-labelledby={`${idBase}-nome`}
      className="flex min-w-0 flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-[var(--lg-sombra-1)] transition-shadow duration-200 hover:shadow-[var(--lg-sombra-2)] motion-reduce:transition-none"
    >
      {/* Cabeçalho: nome + status. O interruptor substitui o botão "Pausar/Ativar" e o selo repete o estado em texto. */}
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id={`${idBase}-nome`} className="truncate text-base leading-snug text-foreground" title={campaign.nome}>
            {campaign.nome}
          </h3>
          {campaign.descricao && (
            <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">{campaign.descricao}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={cn('lg-selo', campaign.ativo ? 'lg-selo--sucesso' : 'lg-selo--neutro')}>
            <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', campaign.ativo ? 'bg-success' : 'bg-muted-foreground')} />
            {campaign.ativo ? 'Ativa' : 'Pausada'}
          </span>
          {updateStatus.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Alterando status" />
          ) : (
            <Switch
              checked={campaign.ativo}
              onCheckedChange={(ativo) => updateStatus.mutate({ id: campaign.id, ativo })}
              aria-label={campaign.ativo ? `Pausar a campanha ${campaign.nome}` : `Ativar a campanha ${campaign.nome}`}
            />
          )}
        </div>
      </header>

      {/* Quando dispara: uma frase só, no lugar de 3 etiquetas separadas. */}
      <p className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground">
        <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" aria-hidden="true" />
        {resumirGatilhoFollowup(campaign)}
      </p>

      {/* Linha do tempo da sequência: cada mensagem é um botão que abre a prévia abaixo. */}
      {passos.length > 0 ? (
        <div className="space-y-2">
          <p className="lg-rotulo">
            Sequência · {passos.length} {passos.length === 1 ? 'mensagem' : 'mensagens'}
          </p>
          <ol className="flex flex-wrap items-center gap-1.5" aria-label={`Sequência da campanha ${campaign.nome}`}>
            {passos.map((passo, indice) => {
              const aberto = passoAberto === passo.sequencia;
              return (
                <li key={passo.sequencia} className="flex items-center gap-1.5">
                  {indice > 0 && <span aria-hidden="true" className="h-px w-3 bg-border" />}
                  <button
                    type="button"
                    onClick={() => setPassoAberto(aberto ? null : passo.sequencia)}
                    aria-expanded={aberto}
                    aria-controls={idPrevia}
                    className={cn(
                      'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      aberto
                        ? 'border-transparent bg-foreground text-background'
                        : 'border-border bg-muted text-foreground hover:border-input hover:bg-accent',
                    )}
                  >
                    <span className="font-semibold">{passo.sequencia}</span>
                    <span className={aberto ? 'text-background/80' : 'text-muted-foreground'}>{passo.momento}</span>
                    {passo.usaIa && <Sparkles className="h-3 w-3" aria-label="escrita pela IA" />}
                    {passo.temImagem && <ImageIcon className="h-3 w-3" aria-label="com imagem" />}
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Prévia da mensagem escolhida: entra com fade curto (só opacidade/transform). */}
          <div id={idPrevia} aria-live="polite">
            {passoSelecionado && (
              <div
                key={passoSelecionado.sequencia}
                className="mt-1 rounded-2xl bg-muted p-3 text-[13px] leading-relaxed text-foreground animate-in fade-in-0 slide-in-from-top-1 duration-200 motion-reduce:animate-none"
              >
                <p className="mb-1 text-xs font-semibold text-muted-foreground">
                  Mensagem {passoSelecionado.sequencia} · {passoSelecionado.titulo}
                </p>
                {passoSelecionado.usaIa ? (
                  <p className="flex items-start gap-1.5">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-foreground" aria-hidden="true" />
                    A IA escreve esta mensagem na hora do envio, com base na conversa do lead.
                  </p>
                ) : (
                  <p className="whitespace-pre-line">{passoSelecionado.conteudo || 'Sem texto configurado.'}</p>
                )}
                {passoSelecionado.temImagem && (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Vai com uma imagem anexada.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="rounded-2xl bg-warning-subtle px-3 py-2 text-[13px] text-warning-text">
          Esta campanha ainda não tem mensagens. Edite para montar a sequência.
        </p>
      )}

      {/* Só as restrições que estão ligadas, em etiquetas neutras e discretas. */}
      <ul className="flex flex-wrap gap-1.5" aria-label="Regras de segurança">
        {restricoes.map((restricao) => (
          <li
            key={restricao}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs text-muted-foreground"
          >
            <ShieldCheck className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{restricao}</span>
          </li>
        ))}
      </ul>

      {/* Ações no rodapé: editar é a ação comum; excluir fica discreto e sempre pede confirmação. */}
      <footer className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-full text-danger-text hover:bg-danger-subtle hover:text-danger-text"
                  disabled={removeCampaign.isPending}
                  aria-label={`Excluir a campanha ${campaign.nome}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
            </TooltipTrigger>
            <TooltipContent>Excluir campanha</TooltipContent>
          </Tooltip>
          <AlertDialogContent className="gens-escopo">
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir a campanha “{campaign.nome}”?</AlertDialogTitle>
              <AlertDialogDescription>
                As mensagens da sequência e o histórico de envios desta campanha serão apagados. Não dá para desfazer.
                Se quiser só parar os envios, use o interruptor para pausar.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removeCampaign.isPending} className="rounded-full">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(evento) => {
                  // Mantém o modal aberto até o banco confirmar; o fechamento acontece no onSuccess.
                  evento.preventDefault();
                  handleConfirmRemove();
                }}
                disabled={removeCampaign.isPending}
                className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {removeCampaign.isPending ? 'Excluindo...' : 'Excluir campanha'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* Abre uma cópia para revisão; duplicar não ativa envios nem altera a campanha original. */}
        <Button
          type="button"
          variant="outline"
          className="h-11 rounded-full px-4 font-semibold"
          aria-label={`Duplicar a campanha ${campaign.nome}`}
          onClick={() => onDuplicate(campaign)}
        >
          <Copy className="h-4 w-4" aria-hidden="true" />
          Duplicar
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-full px-4 font-semibold"
          onClick={() => onEdit(campaign)}
        >
          <PencilLine className="h-4 w-4" />
          Editar
        </Button>
      </footer>
    </article>
  );
};
